import React, {
  ReactElement,
  useEffect,
  useState,
  useContext,
  useMemo,
} from 'react'
import { toAscii, toHalfwidthKana } from 'japanese-string-utils'
import { useNavigate, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { Path as BreadcrumbPath } from 'src/components/molecules/BreadcrumbsPipe'
import { ToastTriggerContext } from 'src/context/toast.context'
import useAppTitle from 'src/hooks/useAppTitle'
import {
  useFilteredCalculationItems,
  useFilteredCalculationDSLs,
} from 'src/hooks/useFilteredCalculationData'
import Path, { TenantPathParams } from 'src/routes/path'
import {
  getCalculationDSLs,
  selectCalculationDSLsRequestStateByParams,
  selectDenormalizedCalculationDSLsByParams,
} from 'src/slices/calculationDSLs/calculationDSLsSlice'
import {
  getCalculationGroup,
  selectCalculationGroupStateByParams,
} from 'src/slices/calculationGroups/calculationGroupsSlice'
import { selectCalculationItemsByParams } from 'src/slices/calculationItems/calculationItemsSlice'
import {
  clearEditCalculation,
  EditCalculation,
  postTenantsIdCalculationDsl,
} from 'src/slices/editCalculations/editCalculationsSlice'
import { OCRFormat } from 'src/slices/services/api'
import {
  getStore,
  selectStoresRequestStateByParams,
  selectStoreByCode,
} from 'src/slices/stores/storesSlice'
import {
  getTenant,
  selectTenantStateByCode,
  selectTenantByCode,
} from 'src/slices/tenants/tenantsSlice'
import { useAppDispatch, useAppSelector } from 'src/store'
import EditCalculationDSLTemplate from '../templates'
import Presenter from './presenter'

const EditCalculationDSL: React.FC = (): ReactElement => {
  useAppTitle('利用設定 ステップ3')
  const toastContext = useContext(ToastTriggerContext)
  const { orgCode, storeCode, tenantCode } =
    useParams<TenantPathParams>() as TenantPathParams
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const storeParams = useMemo(
    () => ({ orgCode, storeCode }),
    [orgCode, storeCode]
  )
  const tenantParams = useMemo(
    () => ({ orgCode, storeCode, tenantCode }),
    [orgCode, storeCode, tenantCode]
  )
  const store = useAppSelector(selectStoreByCode(storeParams))
  const tenant = useAppSelector(selectTenantByCode(tenantParams))
  // 三井不動産（orgCode: 007）では非表示項目をフィルタリング（表示用）
  const calculationItems = useFilteredCalculationItems(storeParams)
  // API送信用に全項目を取得（フィルタリングなし）
  const allCalculationItems = useAppSelector(
    selectCalculationItemsByParams(storeParams)
  )

  const calculationItemsState = useAppSelector(
    (state) => state.entities.calculationItems
  )

  const submitStatus = useAppSelector(
    (state) => state.forms.editCalculations.status
  )

  const { editOCRFormats } = useAppSelector(
    (state) => state.forms.editCalculations
  )

  // 三井不動産（orgCode: 007）では非表示項目をフィルタリング（表示用）
  const dsls = useFilteredCalculationDSLs(tenantParams)
  // API送信用に全DSLを取得（フィルタリングなし）
  const allDsls = useAppSelector(
    selectDenormalizedCalculationDSLsByParams(tenantParams)
  )

  const tenantRequestState = useAppSelector(
    selectTenantStateByCode({ orgCode, storeCode, tenantCode })
  )
  const storeRequestState = useAppSelector(
    selectStoresRequestStateByParams({ orgCode, storeCode })
  )
  const calculationGroupRequestState = useAppSelector(
    selectCalculationGroupStateByParams({ orgCode, storeCode })
  )
  const { status } = useAppSelector(
    selectCalculationDSLsRequestStateByParams({
      orgCode,
      storeCode,
      tenantCode,
    })
  )

  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbPath[]>([])
  useEffect(() => {
    setBreadcrumbs(Presenter.breadcrumbs(store?.name, tenant?.name))
  }, [store?.name, tenant?.name])

  // GET API
  useEffect(() => {
    if (tenantRequestState.status === 'idle') {
      dispatch(getTenant({ orgCode, storeCode, tenantCode }))
    }
  }, [dispatch, orgCode, storeCode, tenantCode, tenantRequestState.status])
  useEffect(() => {
    if (store === undefined && storeRequestState.status === 'idle') {
      dispatch(getStore({ orgCode, storeCode }))
    }
  }, [dispatch, orgCode, store, storeCode, storeRequestState.status])
  useEffect(() => {
    if (calculationGroupRequestState.status === 'idle') {
      dispatch(getCalculationGroup({ orgCode, storeCode }))
    }
  }, [dispatch, orgCode, storeCode, calculationGroupRequestState.status])
  useEffect(() => {
    if (status === 'idle') {
      dispatch(getCalculationDSLs({ orgCode, storeCode, tenantCode }))
    }
  }, [status, dispatch, orgCode, storeCode, tenantCode])

  const handleClickGoBack = () => {
    navigate(-1)
  }

  const handleClickConfirmClose = () => {
    dispatch(clearEditCalculation())
    navigate(Path.tenantCalculationTab(orgCode, storeCode, tenantCode))
  }

  const convertOCRFormats = (calculationId: string): OCRFormat[] => {
    return editOCRFormats
      .filter((editOCRFormat) => editOCRFormat.calculationId === calculationId)
      .map((editOCRFormat) => {
        return {
          id: editOCRFormat.id,
          readItem: toAscii(
            toHalfwidthKana(
              editOCRFormat.readItem ? editOCRFormat.readItem.trim() : ''
            )
          ),
          isDuplicated: editOCRFormat.isDuplicated ?? false,
          sectionName: editOCRFormat.sectionName,
          suffix: editOCRFormat.suffix,
          prefix: editOCRFormat.prefix,
          readTargetNumberColumn: editOCRFormat.readTargetNumberColumn,
          readTargetNumberLine: editOCRFormat.readTargetNumberLine,
        }
      })
  }

  const getEditCalculations = (convertedDSLs: string[]): EditCalculation[] => {
    if (!allCalculationItems) {
      return []
    }
    // フィルタリングされた項目のIDセット（フォームに表示されている項目）
    const visibleCalculationItemIds = new Set(
      calculationItems?.map((item) => item.id) ?? []
    )

    let visibleIndex = 0
    return allCalculationItems.map((calcItem) => {
      const calculationId = calcItem.id
      const isVisible = visibleCalculationItemIds.has(calculationId)

      if (isVisible) {
        // 表示されている項目はフォームの値を使用
        const editDSL = convertedDSLs[visibleIndex]
        const dsl = editDSL || calcItem.fixedDsl || '0'
        visibleIndex += 1
        return {
          calculationId,
          dsl,
          ocrFormats: convertOCRFormats(calculationId),
        }
      }
      // 非表示項目は既存のDSLとocrFormatsを保持
      const existingDsl = allDsls?.find(
        (d) => d.calculationItem.id === calculationId
      )
      // ocrFormatsのIDを新規UUIDに置き換え、DSL内の参照も同時に更新する
      const idMapping = new Map<string, string>(
        existingDsl?.ocrFormats?.map((f) => [f.id, uuidv4()]) ?? []
      )
      const newOcrFormats =
        existingDsl?.ocrFormats?.map((f) => ({
          ...f,
          id: idMapping.get(f.id)!,
        })) ?? []
      const originalDsl = existingDsl?.dsl ?? calcItem.fixedDsl ?? '0'
      const newDsl = Array.from(idMapping.entries()).reduce(
        (dslStr, [oldId, newId]) =>
          dslStr.replaceAll(`{{${oldId}}}`, `{{${newId}}}`),
        originalDsl
      )
      return {
        calculationId,
        dsl: newDsl,
        ocrFormats: newOcrFormats,
      }
    })
  }

  const handleClickGoForward = async (convertedDSLs: string[]) => {
    if (!tenant) {
      return
    }

    const params = {
      tenantId: tenant.id,
      editCalculations: getEditCalculations(convertedDSLs),
    }

    try {
      await dispatch(postTenantsIdCalculationDsl(params)).unwrap()
      navigate(Path.completeEditCalculation(orgCode, storeCode, tenantCode))
    } catch (e) {
      const errorMessage = Presenter.convertErrorToMessage(
        Presenter.convertErrorType(e),
        calculationItemsState
      )
      toastContext.sendToast({
        variant: 'error',
        title: errorMessage,
      })
    }
  }

  return (
    <EditCalculationDSLTemplate
      breadcrumbs={breadcrumbs}
      editOCRFormats={editOCRFormats}
      dsls={dsls}
      calculationItems={calculationItems}
      isSubmitting={submitStatus === 'loading'}
      onClickGoForward={handleClickGoForward}
      onClickGoBack={handleClickGoBack}
      onClickConfirmClose={handleClickConfirmClose}
    />
  )
}

export default EditCalculationDSL
